// Fragment "pathways": the practitioner's side of Pathways v3 (contract §9), part 1: the
// list (admin/PathwaysAdmin.jsx), the shared vocabularies (admin/pathways/labels.js), the
// builder shell and its Basics and Structure steps (admin/pathways/PathwayBuilder.jsx,
// BasicsPanel.jsx, StructureCanvas.jsx), and the `path.error.*` shape errors produced by
// pathways/builderModel.js.
// Also: the shared quiz question editor's one key (components/learn/QuizQuestionsEditor.jsx)
// and the `auto.trigger.pathway.*` labels of the automation triggers the pathway service
// emits (backend modules/automation AVAILABLE_TRIGGERS).
// The step editor is in ./pathwaysMaterials.js; the dashboard, preview and CRM tab in
// ./pathwaysWorkspace.js. Merged in ./index.js.
export default {
  fr: {
    // ── list ──
    "path.title": "Parcours",
    "path.create": "Créer un parcours",
    "path.emptyState": "Transformez vos vidéos, lectures et quiz en un parcours guidé que vos clients suivent pas à pas. Créez votre premier parcours.",
    "path.planNotice": "Votre formule ne permet pas de créer ni de modifier des parcours. Les apprenants déjà inscrits gardent leur accès.",
    "path.loading": "Chargement…",
    "path.retry": "Réessayer",
    "path.notFound": "Ce parcours n'existe plus.",
    "path.card.enrolledOf": "{n} / {capacity} inscrits",
    "path.card.enrolled": "{n} inscrits",
    "path.card.avg": "Progression moyenne {p} %",
    "path.card.avgNone": "Progression moyenne —",
    "path.menu.label": "Actions pour « {title} »",
    "path.menu.edit": "Modifier",
    "path.menu.duplicate": "Dupliquer",
    "path.menu.archive": "Archiver",
    "path.confirmArchive": "Archiver « {title} » ? Il disparaît de /parcours et n'accepte plus d'inscriptions. Les apprenants gardent leur accès et leur historique. Un parcours archivé ne peut pas être réactivé.",
    "path.archived": "Parcours archivé",
    "path.duplicated": "Copie créée en brouillon",

    // ── vocabularies ──
    "path.status.draft": "Brouillon",
    "path.status.published": "Actif",
    "path.status.archived": "Archivé",
    "path.type.successive": "Successif",
    "path.type.alternative": "Alternatif",
    "path.type.level": "Par niveaux",
    "path.type.successiveHint": "Terminez chaque étape pour débloquer la suivante",
    "path.type.alternativeHint": "Étapes obligatoires + facultatives : passez ce dont vous n'avez pas besoin",
    "path.type.levelHint": "Des étapes par niveau : terminez un niveau pour débloquer le suivant",
    "path.kind.video": "Vidéo",
    "path.kind.reading": "Lecture",
    "path.kind.quiz": "Quiz",
    "path.kind.practice": "Pratique / atelier",
    "path.kind.live": "Séance en direct",
    "path.kind.course": "Cours",
    "path.level.prefix": "Niveau {n} ·",
    "path.level.label": "Niveau {n} · {title}",
    "path.level.labelUntitled": "Niveau {n}",
    "path.step.mandatory": "Obligatoire",
    "path.step.optional": "Facultative",
    "path.step.minutes": "~{n} min",
    "path.size.kb": "{n} Ko",
    "path.size.mb": "{n} Mo",

    // ── builder shell ──
    "path.builder.back": "Tous les parcours",
    "path.builder.untitled": "Nouveau parcours",
    "path.builder.steps": "Étapes de création",
    "path.builder.tab.basics": "{n} · Informations",
    "path.builder.tab.structure": "{n} · Structure",
    "path.builder.preview": "Aperçu apprenant",
    "path.builder.saveDraft": "Enregistrer le brouillon",
    "path.builder.saveChanges": "Enregistrer",
    "path.builder.activate": "Activer",
    "path.builder.problemsTitle": "Avant de pouvoir activer ce parcours :",
    "path.builder.fixHighlighted": "Corrigez les champs signalés avant d'enregistrer.",
    "path.builder.archivedReadOnly": "Ce parcours est archivé : il reste consultable, mais ne peut plus être modifié.",
    "path.unsaved": "Modifications non enregistrées",
    "path.allSaved": "Tout est enregistré",
    "path.saved": "Parcours enregistré",
    "path.activated": "Parcours activé : il est visible sur /parcours.",
    "path.activateRefused": "Le brouillon est enregistré, mais il ne peut pas encore être activé.",
    "path.confirmDiscard": "Quitter sans enregistrer ? Vos modifications seront perdues.",

    // ── Basics ──
    "path.basics.name": "Nom",
    "path.basics.namePh": "Ex. : Yoga prénatal, les fondations",
    "path.basics.description": "Description (affichée sur /parcours)",
    "path.basics.objectives": "Objectifs (1 obligatoire, 6 au maximum)",
    "path.basics.objectivePh": "À la fin, vous saurez…",
    "path.basics.objectiveN": "Objectif {n}",
    "path.basics.objectiveRemove": "Retirer l'objectif {n}",
    "path.basics.objectiveAdd": "Ajouter un objectif",
    "path.basics.certificate": "Certificat",
    "path.basics.certificateHint": "PDF envoyé par e-mail une fois toutes les étapes obligatoires terminées",
    "path.basics.pathType": "Type de parcours",
    "path.basics.typeLockNote": "Verrouillé une fois actif avec des membres",
    "path.basics.typeLockedNow": "Verrouillé : ce parcours est actif et compte des membres.",
    "path.basics.mergeConfirm": "Passer en « {type} » fusionne vos {n} niveaux en une seule liste, dans l'ordre. Continuer ?",
    "path.basics.schedule": "Calendrier",
    "path.basics.schedule.cohort": "Cohorte",
    "path.basics.schedule.self_paced": "À son rythme",
    "path.basics.cohortHint": "Dates de début et de fin, tout le monde avance ensemble, messages hebdomadaires disponibles.",
    "path.basics.selfPacedHint": "Chaque apprenant commence dès son inscription.",
    "path.basics.startDate": "Date de début",
    "path.basics.endDate": "Date de fin",
    "path.basics.capacity": "Capacité",
    "path.basics.capacityPh": "Illimitée",
    "path.basics.price": "Prix (0 = gratuit)",

    // ── Structure ──
    "path.structure.addLevel": "Ajouter un niveau",
    "path.structure.addStep": "Ajouter une étape",
    "path.structure.levelTitlePh": "Nommez ce niveau",
    "path.structure.unlocksNow": "Débloqué : immédiatement",
    "path.structure.unlocksAfter": "Débloqué après : {what}",
    "path.structure.noSteps": "Aucune étape pour l'instant.",
    "path.structure.liveNoTime": "Date et heure à fixer",
    "path.structure.liveNoService": "Type de séance à choisir",
    "path.structure.untitledStep": "Étape sans titre",
    "path.structure.needsAttention": "À compléter",
    "path.structure.dragLevel": "Glisser pour réordonner le niveau",
    "path.structure.dragStep": "Glisser pour réordonner l'étape",
    "path.structure.levelUp": "Monter le niveau {n}",
    "path.structure.levelDown": "Descendre le niveau {n}",
    "path.structure.levelRemove": "Supprimer le niveau {n}",
    "path.structure.confirmRemoveLevelOne": "Supprimer le niveau {n} et son étape ?",
    "path.structure.confirmRemoveLevel": "Supprimer le niveau {n} et ses {count} étapes ?",
    "path.structure.stepUp": "Monter « {title} »",
    "path.structure.stepDown": "Descendre « {title} »",
    "path.structure.stepRemove": "Supprimer « {title} »",
    "path.structure.confirmRemoveStep": "Supprimer l'étape « {title} » ?",
    "path.structure.pillMandatoryAria": "« {title} » est obligatoire : la rendre facultative",
    "path.structure.pillOptionalAria": "« {title} » est facultative : la rendre obligatoire",

    // ── shape errors (pathways/builderModel.js) ──
    "path.error.title": "Donnez un nom au parcours (120 caractères au maximum).",
    "path.error.description": "La description est trop longue (5 000 caractères au maximum).",
    "path.error.objectivesMax": "6 objectifs au maximum.",
    "path.error.objectiveLength": "Un objectif fait 200 caractères au maximum.",
    "path.error.capacity": "La capacité doit être un nombre entier entre 1 et 100 000, ou vide pour illimitée.",
    "path.error.price": "Le prix doit être un montant entre 0 et 100 000, avec au plus deux décimales.",
    "path.error.priceMinimum": "Un parcours payant coûte au moins 0,50. Mettez 0 pour un parcours gratuit.",
    "path.error.cohortDates": "La date de fin doit suivre la date de début.",
    "path.error.tooManyLevels": "20 niveaux au maximum.",
    "path.error.tooManySteps": "200 étapes au maximum pour un parcours.",
    "path.error.tooManyLevelSteps": "50 étapes au maximum par niveau.",
    "path.error.levelTitle": "Nommez ce niveau (120 caractères au maximum).",
    "path.error.stepInvalid": "Cette étape est incomplète : ouvrez-la pour la corriger.",
    "path.error.stepTitle": "Donnez un titre à l'étape (160 caractères au maximum).",
    "path.error.duration": "La durée est un nombre de minutes entre 0 et 1 440.",
    "path.error.videoUrl": "Collez une adresse YouTube ou Vimeo.",
    "path.error.readingBody": "Le texte est trop long (20 000 caractères au maximum).",
    "path.error.threshold": "Le seuil de réussite est un pourcentage entre 0 et 100.",
    "path.error.tooManyQuestions": "50 questions au maximum.",
    "path.error.questionText": "Chaque question a un texte (1 000 caractères au maximum).",
    "path.error.choicesCount": "Chaque question a entre 2 et 6 réponses.",
    "path.error.choiceText": "Chaque réponse a un texte (300 caractères au maximum).",
    "path.error.correctChoice": "Indiquez la bonne réponse de chaque question.",
    "path.error.tooManyResources": "10 fichiers au maximum.",
    "path.error.practiceInstructions": "5 000 caractères au maximum pour les consignes.",
    "path.error.locationNote": "300 caractères au maximum pour le lieu.",
    "path.error.startsAt": "Cette date et heure n'est pas valide.",
    "path.error.courseRequired": "Choisissez le cours de cette étape.",

    // ── shared quiz editor / automation ──
    "learn.removeQuestion": "Supprimer la question",
    "auto.trigger.pathway.enrolled": "Inscription à un parcours",
    "auto.trigger.pathway.step_completed": "Étape de parcours terminée",
    "auto.trigger.pathway.level_completed": "Niveau de parcours terminé",
    "auto.trigger.pathway.completed": "Parcours terminé",
  },
  en: {
    // ── list ──
    "path.title": "Pathways",
    "path.create": "Create pathway",
    "path.emptyState": "Turn your videos, readings and quizzes into a guided path your clients follow step by step. Create your first pathway.",
    "path.planNotice": "Your plan does not include creating or editing pathways. Learners already enrolled keep their access.",
    "path.loading": "Loading…",
    "path.retry": "Try again",
    "path.notFound": "This pathway no longer exists.",
    "path.card.enrolledOf": "{n} / {capacity} enrolled",
    "path.card.enrolled": "{n} enrolled",
    "path.card.avg": "Avg progress {p}%",
    "path.card.avgNone": "Avg progress —",
    "path.menu.label": "Actions for “{title}”",
    "path.menu.edit": "Edit",
    "path.menu.duplicate": "Duplicate",
    "path.menu.archive": "Archive",
    "path.confirmArchive": "Archive “{title}”? It disappears from /parcours and takes no new enrolments. Learners keep their access and their history. An archived pathway cannot be reactivated.",
    "path.archived": "Pathway archived",
    "path.duplicated": "Copy created as a draft",

    // ── vocabularies ──
    "path.status.draft": "Draft",
    "path.status.published": "Active",
    "path.status.archived": "Archived",
    "path.type.successive": "Successive",
    "path.type.alternative": "Alternative",
    "path.type.level": "Level",
    "path.type.successiveHint": "Finish each step to unlock the next",
    "path.type.alternativeHint": "Required + optional steps, skip what you don't need",
    "path.type.levelHint": "Stages; finish a level to unlock the next",
    "path.kind.video": "Video",
    "path.kind.reading": "Reading",
    "path.kind.quiz": "Quiz",
    "path.kind.practice": "Practice/Lab",
    "path.kind.live": "Live session",
    "path.kind.course": "Course",
    "path.level.prefix": "Level {n} ·",
    "path.level.label": "Level {n} · {title}",
    "path.level.labelUntitled": "Level {n}",
    "path.step.mandatory": "Mandatory",
    "path.step.optional": "Optional",
    "path.step.minutes": "~{n} min",
    "path.size.kb": "{n} KB",
    "path.size.mb": "{n} MB",

    // ── builder shell ──
    "path.builder.back": "All pathways",
    "path.builder.untitled": "New pathway",
    "path.builder.steps": "Builder steps",
    "path.builder.tab.basics": "{n} · Basics",
    "path.builder.tab.structure": "{n} · Structure",
    "path.builder.preview": "Preview as learner",
    "path.builder.saveDraft": "Save draft",
    "path.builder.saveChanges": "Save changes",
    "path.builder.activate": "Activate",
    "path.builder.problemsTitle": "Before this pathway can be activated:",
    "path.builder.fixHighlighted": "Fix the highlighted fields before saving.",
    "path.builder.archivedReadOnly": "This pathway is archived: you can still read it, but it can no longer be changed.",
    "path.unsaved": "Unsaved changes",
    "path.allSaved": "All changes saved",
    "path.saved": "Pathway saved",
    "path.activated": "Pathway activated: it is now listed on /parcours.",
    "path.activateRefused": "The draft is saved, but it cannot be activated yet.",
    "path.confirmDiscard": "Leave without saving? Your changes will be lost.",

    // ── Basics ──
    "path.basics.name": "Name",
    "path.basics.namePh": "e.g. Prenatal Yoga Foundations",
    "path.basics.description": "Description (shown on /parcours)",
    "path.basics.objectives": "Objectives (1 required, max 6)",
    "path.basics.objectivePh": "By the end you will be able to…",
    "path.basics.objectiveN": "Objective {n}",
    "path.basics.objectiveRemove": "Remove objective {n}",
    "path.basics.objectiveAdd": "Add objective",
    "path.basics.certificate": "Certificate",
    "path.basics.certificateHint": "PDF emailed on completing all mandatory steps",
    "path.basics.pathType": "Path type",
    "path.basics.typeLockNote": "Locked once Active with members",
    "path.basics.typeLockedNow": "Locked: this pathway is Active and has members.",
    "path.basics.mergeConfirm": "Switching to {type} merges your {n} levels into one list, in order. Continue?",
    "path.basics.schedule": "Schedule",
    "path.basics.schedule.cohort": "Cohort",
    "path.basics.schedule.self_paced": "Self-paced",
    "path.basics.cohortHint": "Start and end dates, everyone together, weekly prompts available.",
    "path.basics.selfPacedHint": "Each learner starts when they enrol.",
    "path.basics.startDate": "Start date",
    "path.basics.endDate": "End date",
    "path.basics.capacity": "Capacity",
    "path.basics.capacityPh": "Unlimited",
    "path.basics.price": "Price (0 = free)",

    // ── Structure ──
    "path.structure.addLevel": "Add level",
    "path.structure.addStep": "Add step",
    "path.structure.levelTitlePh": "Name this level",
    "path.structure.unlocksNow": "Unlocks: immediately",
    "path.structure.unlocksAfter": "Unlocks after: {what}",
    "path.structure.noSteps": "No steps yet.",
    "path.structure.liveNoTime": "Date and time not set",
    "path.structure.liveNoService": "Session type not chosen",
    "path.structure.untitledStep": "Untitled step",
    "path.structure.needsAttention": "Needs attention",
    "path.structure.dragLevel": "Drag to reorder the level",
    "path.structure.dragStep": "Drag to reorder the step",
    "path.structure.levelUp": "Move level {n} up",
    "path.structure.levelDown": "Move level {n} down",
    "path.structure.levelRemove": "Remove level {n}",
    "path.structure.confirmRemoveLevelOne": "Remove level {n} and its step?",
    "path.structure.confirmRemoveLevel": "Remove level {n} and its {count} steps?",
    "path.structure.stepUp": "Move “{title}” up",
    "path.structure.stepDown": "Move “{title}” down",
    "path.structure.stepRemove": "Remove “{title}”",
    "path.structure.confirmRemoveStep": "Remove the step “{title}”?",
    "path.structure.pillMandatoryAria": "“{title}” is mandatory: make it optional",
    "path.structure.pillOptionalAria": "“{title}” is optional: make it mandatory",

    // ── shape errors (pathways/builderModel.js) ──
    "path.error.title": "Give the pathway a name (120 characters at most).",
    "path.error.description": "The description is too long (5,000 characters at most).",
    "path.error.objectivesMax": "6 objectives at most.",
    "path.error.objectiveLength": "An objective is 200 characters at most.",
    "path.error.capacity": "Capacity must be a whole number from 1 to 100,000, or empty for unlimited.",
    "path.error.price": "The price must be an amount from 0 to 100,000, with at most two decimals.",
    "path.error.priceMinimum": "A paid pathway costs at least 0.50. Enter 0 for a free pathway.",
    "path.error.cohortDates": "The end date must come after the start date.",
    "path.error.tooManyLevels": "20 levels at most.",
    "path.error.tooManySteps": "200 steps at most in a pathway.",
    "path.error.tooManyLevelSteps": "50 steps at most in a level.",
    "path.error.levelTitle": "Name this level (120 characters at most).",
    "path.error.stepInvalid": "This step is incomplete: open it to fix it.",
    "path.error.stepTitle": "Give the step a title (160 characters at most).",
    "path.error.duration": "The duration is a number of minutes from 0 to 1,440.",
    "path.error.videoUrl": "Paste a YouTube or Vimeo address.",
    "path.error.readingBody": "The text is too long (20,000 characters at most).",
    "path.error.threshold": "The pass threshold is a percentage from 0 to 100.",
    "path.error.tooManyQuestions": "50 questions at most.",
    "path.error.questionText": "Every question needs its text (1,000 characters at most).",
    "path.error.choicesCount": "Every question has 2 to 6 choices.",
    "path.error.choiceText": "Every choice needs its text (300 characters at most).",
    "path.error.correctChoice": "Mark the correct choice of every question.",
    "path.error.tooManyResources": "10 files at most.",
    "path.error.practiceInstructions": "5,000 characters at most for the instructions.",
    "path.error.locationNote": "300 characters at most for the location.",
    "path.error.startsAt": "This date and time is not valid.",
    "path.error.courseRequired": "Choose the course for this step.",

    // ── shared quiz editor / automation ──
    "learn.removeQuestion": "Remove question",
    "auto.trigger.pathway.enrolled": "Enrolled in a pathway",
    "auto.trigger.pathway.step_completed": "Pathway step completed",
    "auto.trigger.pathway.level_completed": "Pathway level completed",
    "auto.trigger.pathway.completed": "Pathway completed",
  },
  es: {
    // ── list ──
    "path.title": "Itinerarios",
    "path.create": "Crear un itinerario",
    "path.emptyState": "Convierte tus vídeos, lecturas y cuestionarios en un itinerario guiado que tus clientes siguen paso a paso. Crea tu primer itinerario.",
    "path.planNotice": "Tu plan no permite crear ni modificar itinerarios. Los alumnos ya inscritos conservan su acceso.",
    "path.loading": "Cargando…",
    "path.retry": "Reintentar",
    "path.notFound": "Este itinerario ya no existe.",
    "path.card.enrolledOf": "{n} / {capacity} inscritos",
    "path.card.enrolled": "{n} inscritos",
    "path.card.avg": "Progreso medio {p} %",
    "path.card.avgNone": "Progreso medio —",
    "path.menu.label": "Acciones para «{title}»",
    "path.menu.edit": "Editar",
    "path.menu.duplicate": "Duplicar",
    "path.menu.archive": "Archivar",
    "path.confirmArchive": "¿Archivar «{title}»? Desaparece de /parcours y no admite nuevas inscripciones. Los alumnos conservan su acceso y su historial. Un itinerario archivado no se puede reactivar.",
    "path.archived": "Itinerario archivado",
    "path.duplicated": "Copia creada como borrador",

    // ── vocabularies ──
    "path.status.draft": "Borrador",
    "path.status.published": "Activo",
    "path.status.archived": "Archivado",
    "path.type.successive": "Sucesivo",
    "path.type.alternative": "Alternativo",
    "path.type.level": "Por niveles",
    "path.type.successiveHint": "Termina cada paso para desbloquear el siguiente",
    "path.type.alternativeHint": "Pasos obligatorios + opcionales: sáltate lo que no necesites",
    "path.type.levelHint": "Etapas: termina un nivel para desbloquear el siguiente",
    "path.kind.video": "Vídeo",
    "path.kind.reading": "Lectura",
    "path.kind.quiz": "Cuestionario",
    "path.kind.practice": "Práctica / taller",
    "path.kind.live": "Sesión en directo",
    "path.kind.course": "Curso",
    "path.level.prefix": "Nivel {n} ·",
    "path.level.label": "Nivel {n} · {title}",
    "path.level.labelUntitled": "Nivel {n}",
    "path.step.mandatory": "Obligatorio",
    "path.step.optional": "Opcional",
    "path.step.minutes": "~{n} min",
    "path.size.kb": "{n} KB",
    "path.size.mb": "{n} MB",

    // ── builder shell ──
    "path.builder.back": "Todos los itinerarios",
    "path.builder.untitled": "Nuevo itinerario",
    "path.builder.steps": "Pasos de creación",
    "path.builder.tab.basics": "{n} · Información",
    "path.builder.tab.structure": "{n} · Estructura",
    "path.builder.preview": "Vista del alumno",
    "path.builder.saveDraft": "Guardar borrador",
    "path.builder.saveChanges": "Guardar cambios",
    "path.builder.activate": "Activar",
    "path.builder.problemsTitle": "Antes de poder activar este itinerario:",
    "path.builder.fixHighlighted": "Corrige los campos señalados antes de guardar.",
    "path.builder.archivedReadOnly": "Este itinerario está archivado: puedes consultarlo, pero ya no se puede modificar.",
    "path.unsaved": "Cambios sin guardar",
    "path.allSaved": "Todo guardado",
    "path.saved": "Itinerario guardado",
    "path.activated": "Itinerario activado: ya aparece en /parcours.",
    "path.activateRefused": "El borrador está guardado, pero todavía no se puede activar.",
    "path.confirmDiscard": "¿Salir sin guardar? Se perderán tus cambios.",

    // ── Basics ──
    "path.basics.name": "Nombre",
    "path.basics.namePh": "Ej.: Yoga prenatal, los fundamentos",
    "path.basics.description": "Descripción (se muestra en /parcours)",
    "path.basics.objectives": "Objetivos (1 obligatorio, 6 como máximo)",
    "path.basics.objectivePh": "Al terminar, sabrás…",
    "path.basics.objectiveN": "Objetivo {n}",
    "path.basics.objectiveRemove": "Quitar el objetivo {n}",
    "path.basics.objectiveAdd": "Añadir un objetivo",
    "path.basics.certificate": "Certificado",
    "path.basics.certificateHint": "PDF enviado por correo al completar todos los pasos obligatorios",
    "path.basics.pathType": "Tipo de itinerario",
    "path.basics.typeLockNote": "Bloqueado una vez activo con miembros",
    "path.basics.typeLockedNow": "Bloqueado: este itinerario está activo y tiene miembros.",
    "path.basics.mergeConfirm": "Pasar a «{type}» fusiona tus {n} niveles en una sola lista, en orden. ¿Continuar?",
    "path.basics.schedule": "Calendario",
    "path.basics.schedule.cohort": "Cohorte",
    "path.basics.schedule.self_paced": "A tu ritmo",
    "path.basics.cohortHint": "Fechas de inicio y fin, todos avanzan juntos, mensajes semanales disponibles.",
    "path.basics.selfPacedHint": "Cada alumno empieza al inscribirse.",
    "path.basics.startDate": "Fecha de inicio",
    "path.basics.endDate": "Fecha de fin",
    "path.basics.capacity": "Capacidad",
    "path.basics.capacityPh": "Ilimitada",
    "path.basics.price": "Precio (0 = gratis)",

    // ── Structure ──
    "path.structure.addLevel": "Añadir un nivel",
    "path.structure.addStep": "Añadir un paso",
    "path.structure.levelTitlePh": "Pon nombre a este nivel",
    "path.structure.unlocksNow": "Se desbloquea: inmediatamente",
    "path.structure.unlocksAfter": "Se desbloquea tras: {what}",
    "path.structure.noSteps": "Todavía no hay pasos.",
    "path.structure.liveNoTime": "Fecha y hora por fijar",
    "path.structure.liveNoService": "Tipo de sesión por elegir",
    "path.structure.untitledStep": "Paso sin título",
    "path.structure.needsAttention": "Por completar",
    "path.structure.dragLevel": "Arrastra para reordenar el nivel",
    "path.structure.dragStep": "Arrastra para reordenar el paso",
    "path.structure.levelUp": "Subir el nivel {n}",
    "path.structure.levelDown": "Bajar el nivel {n}",
    "path.structure.levelRemove": "Eliminar el nivel {n}",
    "path.structure.confirmRemoveLevelOne": "¿Eliminar el nivel {n} y su paso?",
    "path.structure.confirmRemoveLevel": "¿Eliminar el nivel {n} y sus {count} pasos?",
    "path.structure.stepUp": "Subir «{title}»",
    "path.structure.stepDown": "Bajar «{title}»",
    "path.structure.stepRemove": "Eliminar «{title}»",
    "path.structure.confirmRemoveStep": "¿Eliminar el paso «{title}»?",
    "path.structure.pillMandatoryAria": "«{title}» es obligatorio: hacerlo opcional",
    "path.structure.pillOptionalAria": "«{title}» es opcional: hacerlo obligatorio",

    // ── shape errors (pathways/builderModel.js) ──
    "path.error.title": "Ponle un nombre al itinerario (120 caracteres como máximo).",
    "path.error.description": "La descripción es demasiado larga (5000 caracteres como máximo).",
    "path.error.objectivesMax": "6 objetivos como máximo.",
    "path.error.objectiveLength": "Un objetivo tiene 200 caracteres como máximo.",
    "path.error.capacity": "La capacidad debe ser un número entero de 1 a 100 000, o vacía para ilimitada.",
    "path.error.price": "El precio debe ser un importe de 0 a 100 000, con dos decimales como máximo.",
    "path.error.priceMinimum": "Un itinerario de pago cuesta al menos 0,50. Pon 0 para un itinerario gratuito.",
    "path.error.cohortDates": "La fecha de fin debe ser posterior a la de inicio.",
    "path.error.tooManyLevels": "20 niveles como máximo.",
    "path.error.tooManySteps": "200 pasos como máximo en un itinerario.",
    "path.error.tooManyLevelSteps": "50 pasos como máximo por nivel.",
    "path.error.levelTitle": "Pon nombre a este nivel (120 caracteres como máximo).",
    "path.error.stepInvalid": "Este paso está incompleto: ábrelo para corregirlo.",
    "path.error.stepTitle": "Ponle un título al paso (160 caracteres como máximo).",
    "path.error.duration": "La duración es un número de minutos de 0 a 1440.",
    "path.error.videoUrl": "Pega una dirección de YouTube o Vimeo.",
    "path.error.readingBody": "El texto es demasiado largo (20 000 caracteres como máximo).",
    "path.error.threshold": "El umbral de aprobado es un porcentaje de 0 a 100.",
    "path.error.tooManyQuestions": "50 preguntas como máximo.",
    "path.error.questionText": "Cada pregunta necesita su texto (1000 caracteres como máximo).",
    "path.error.choicesCount": "Cada pregunta tiene de 2 a 6 respuestas.",
    "path.error.choiceText": "Cada respuesta necesita su texto (300 caracteres como máximo).",
    "path.error.correctChoice": "Marca la respuesta correcta de cada pregunta.",
    "path.error.tooManyResources": "10 archivos como máximo.",
    "path.error.practiceInstructions": "5000 caracteres como máximo para las instrucciones.",
    "path.error.locationNote": "300 caracteres como máximo para el lugar.",
    "path.error.startsAt": "Esta fecha y hora no es válida.",
    "path.error.courseRequired": "Elige el curso de este paso.",

    // ── shared quiz editor / automation ──
    "learn.removeQuestion": "Eliminar la pregunta",
    "auto.trigger.pathway.enrolled": "Inscripción en un itinerario",
    "auto.trigger.pathway.step_completed": "Paso de itinerario completado",
    "auto.trigger.pathway.level_completed": "Nivel de itinerario completado",
    "auto.trigger.pathway.completed": "Itinerario completado",
  },
};
